// Extended https://github.com/react-simple-code-editor/react-simple-code-editor
import { Button, Icon } from '@opengov/component-library';
import cx from 'classnames2';
import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';

import styles from './CodeEditor.scss';

type Padding<T> = T | { top?: T; right?: T; bottom?: T; left?: T };

export type CodeEditorProps = React.HTMLAttributes<HTMLDivElement> & {
	// Props for the component
	value: string;
	tabSize?: number;
	insertSpaces?: boolean;
	ignoreTabKey?: boolean;
	padding?: Padding<number | string>;
	style?: React.CSSProperties;

	// Props for the textarea
	textareaId?: string;
	textareaClassName?: string;
	autoFocus?: boolean;
	disabled?: boolean;
	form?: string;
	maxLength?: number;
	minLength?: number;
	name?: string;
	placeholder?: string;
	readOnly?: boolean;
	required?: boolean;
	onClick?: React.MouseEventHandler<HTMLTextAreaElement>;
	onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
	onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
	onKeyUp?: React.KeyboardEventHandler<HTMLTextAreaElement>;
	onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;

	// Props for the hightlighted code’s pre element
	preClassName?: string;

	// Ogov specific props
	showUndoRedoControls?: boolean;
	showCopyControl?: boolean;
	wrapperClassName?: string;
	contentClassName?: string;
	dataTest?: string;
	onValueChange(value: string): void;
	highlight(value: string): string | React.ReactNode;
};

type State = {
	capture: boolean;
};

type Record = {
	value: string;
	selectionStart: number;
	selectionEnd: number;
};

type History = {
	stack: (Record & { timestamp: number })[];
	offset: number;
};

const KEYCODE_ENTER = 13;
const KEYCODE_TAB = 9;
const KEYCODE_BACKSPACE = 8;
const KEYCODE_Y = 89;
const KEYCODE_Z = 90;
const KEYCODE_M = 77;
const KEYCODE_PARENS = 57;
const KEYCODE_BRACKETS = 219;
const KEYCODE_QUOTE = 222;
const KEYCODE_BACK_QUOTE = 192;
const KEYCODE_ESCAPE = 27;

const HISTORY_LIMIT = 100;
const HISTORY_TIME_GAP = 3000;

const isWindows = window && /Win/i.test(navigator?.platform);
const isMacLike = window && /(Mac|iPhone|iPod|iPad)/i.test(navigator?.platform);

const inlineStyles = {
	textarea: {
		position: 'absolute',
		top: 0,
		left: 0,
		height: '100%',
		width: '100%',
		resize: 'none',
		color: 'inherit',
		overflow: 'hidden',
		MozOsxFontSmoothing: 'grayscale',
		WebkitFontSmoothing: 'antialiased',
		WebkitTextFillColor: 'transparent'
	},
	highlight: {
		position: 'relative',
		pointerEvents: 'none'
	},
	editor: {
		margin: 0,
		border: 0,
		background: 'none',
		boxSizing: 'inherit',
		display: 'inherit',
		fontFamily: 'inherit',
		fontSize: 'inherit',
		fontStyle: 'inherit',
		fontVariantLigatures: 'inherit',
		fontWeight: 'inherit',
		letterSpacing: 'inherit',
		lineHeight: 'inherit',
		tabSize: 'inherit',
		textIndent: 'inherit',
		textRendering: 'inherit',
		textTransform: 'inherit',
		whiteSpace: 'pre-wrap',
		wordBreak: 'keep-all',
		overflowWrap: 'break-word'
	}
} as const;
export class CodeEditor extends React.Component<CodeEditorProps, State> {
	static defaultProps = {
		tabSize: 2,
		insertSpaces: true,
		ignoreTabKey: false,
		padding: 0
	};

	state = {
		capture: true
	};

	private history: History = {
		stack: [],
		offset: -1
	};

	private input: HTMLTextAreaElement | null = null;

	componentDidMount() {
		this.recordCurrentState();
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (nextProps.value !== this.input?.value) {
			this.recordChange({
				value: nextProps.value,
				selectionStart: 0,
				selectionEnd: nextProps.value.length - 1
			});
		}
	}

	public clearHistory = () => {
		setTimeout(() => {
			this.history = {
				stack: [],
				offset: -1
			};
			this.recordCurrentState();
		}, 0);
	};

	render() {
		const {
			value,
			style,
			padding,
			highlight,
			textareaId,
			textareaClassName,
			autoFocus,
			disabled,
			form,
			maxLength,
			minLength,
			name,
			placeholder,
			readOnly,
			required,
			onClick,
			onFocus,
			onBlur,
			onKeyUp,
			preClassName,
			wrapperClassName,
			contentClassName,
			dataTest = 'codeEditor',
			showUndoRedoControls,
			showCopyControl,
			...rest
		} = this.props;

		const contentStyle = {
			paddingTop: typeof padding === 'object' ? padding.top : padding,
			paddingRight: typeof padding === 'object' ? padding.right : padding,
			paddingBottom: typeof padding === 'object' ? padding.bottom : padding,
			paddingLeft: typeof padding === 'object' ? padding.left : padding
		};

		const highlighted = highlight(value);
		const showToolbal = showUndoRedoControls || showCopyControl;

		return (
			<div className={cx(styles.editor, wrapperClassName)} data-test={dataTest}>
				{showToolbal && (
					<div className={styles.editorControls} data-test="codeEditorControls">
						<Button
							dataTest="undoButton"
							className={styles.iconButton}
							variant="naked"
							size="small"
							disabled={!this.canUndo}
							onClick={this.undoEdit}
						>
							<Icon icon="action-undo" className={styles.undoIcon} />
						</Button>
						<Button
							dataTest="redoButton"
							className={styles.iconButton}
							variant="naked"
							size="small"
							disabled={!this.canRedo}
							onClick={this.redoEdit}
						>
							<Icon icon="action-undo" className={styles.redoIcon} />
						</Button>
						<CopyToClipboard text={value}>
							<Button
								dataTest="copyButton"
								className={styles.iconButton}
								variant="naked"
								size="small"
								disabled={!value}
							>
								<Icon icon="action-duplicate" />
							</Button>
						</CopyToClipboard>
					</div>
				)}
				<div className={cx(styles.contentWrapper, { [styles.disabledContent]: disabled }, contentClassName)}>
					<div {...rest} className={styles.content} style={style}>
						<pre
							data-test="codeEditorFormattedCode"
							className={cx(styles.formattedCode, preClassName)}
							aria-hidden="true"
							style={{ ...inlineStyles.editor, ...inlineStyles.highlight, ...contentStyle }}
							{...(typeof highlighted === 'string'
								? { dangerouslySetInnerHTML: { __html: `${highlighted}<br />` } }
								: { children: highlighted })}
						/>
						<textarea
							data-test="codeEditorTextArea"
							ref={(c) => (this.input = c)}
							style={{
								...inlineStyles.editor,
								...inlineStyles.textarea,
								...contentStyle
							}}
							className={cx(styles.textarea, textareaClassName)}
							id={textareaId}
							value={value}
							onChange={this.handleChange}
							onKeyDown={this.handleKeyDown}
							onClick={onClick}
							onKeyUp={onKeyUp}
							onFocus={onFocus}
							onBlur={onBlur}
							disabled={disabled}
							form={form}
							maxLength={maxLength}
							minLength={minLength}
							name={name}
							placeholder={placeholder}
							readOnly={readOnly}
							required={required}
							autoFocus={autoFocus}
							autoCapitalize="off"
							autoComplete="off"
							autoCorrect="off"
							spellCheck={false}
							data-gramm={false}
						/>
					</div>
				</div>
			</div>
		);
	}

	private recordCurrentState = () => {
		const input = this.input;

		if (!input) return;

		// Save current state of the input
		const { value, selectionStart, selectionEnd } = input;

		this.recordChange({
			value,
			selectionStart,
			selectionEnd
		});
	};

	private getLines = (text: string, position: number) => text.substring(0, position).split('\n');

	private recordChange = (record: Record, overwrite = false) => {
		const { stack, offset } = this.history;

		if (stack.length && offset > -1) {
			// When something updates, drop the redo operations
			this.history.stack = stack.slice(0, offset + 1);

			// Limit the number of operations to 100
			const count = this.history.stack.length;

			if (count > HISTORY_LIMIT) {
				const extras = count - HISTORY_LIMIT;

				this.history.stack = stack.slice(extras, count);
				this.history.offset = Math.max(this.history.offset - extras, 0);
			}
		}

		const timestamp = Date.now();

		if (overwrite) {
			const last = this.history.stack[this.history.offset];

			if (last && timestamp - last.timestamp < HISTORY_TIME_GAP) {
				// A previous entry exists and was in short interval

				// Match the last word in the line
				const re = /[^a-z0-9]([a-z0-9]+)$/i;

				// Get the previous line
				const previous = this.getLines(last.value, last.selectionStart).pop()?.match(re);

				// Get the current line
				const current = this.getLines(record.value, record.selectionStart).pop()?.match(re);

				if (previous?.[1] && current?.[1]?.startsWith(previous[1])) {
					// The last word of the previous line and current line match
					// Overwrite previous entry so that undo will remove whole word
					this.history.stack[this.history.offset] = { ...record, timestamp };

					return;
				}
			}
		}

		// Add the new operation to the stack
		this.history.stack.push({ ...record, timestamp });
		this.history.offset++;
	};

	private updateInput = (record: Record) => {
		const input = this.input;

		if (!input) return;

		// Update values and selection state
		input.value = record.value;
		input.selectionStart = record.selectionStart;
		input.selectionEnd = record.selectionEnd;

		this.props.onValueChange(record.value);
	};

	private applyEdits = (record: Record) => {
		// Save last selection state
		const input = this.input;
		const last = this.history.stack[this.history.offset];

		if (last && input) {
			this.history.stack[this.history.offset] = {
				...last,
				selectionStart: input.selectionStart,
				selectionEnd: input.selectionEnd
			};
		}

		// Save the changes
		this.recordChange(record);
		this.updateInput(record);
	};

	get canUndo() {
		const { stack, offset } = this.history;
		return !!stack[offset - 1];
	}
	private undoEdit = () => {
		const { stack, offset } = this.history;
		// Get the previous edit
		const record = stack[offset - 1];
		if (record) {
			// Apply the changes and update the offset
			this.updateInput(record);
			this.history.offset = Math.max(offset - 1, 0);
		}
	};

	get canRedo() {
		const { stack, offset } = this.history;
		return !!stack[offset + 1];
	}
	private redoEdit = () => {
		const { stack, offset } = this.history;

		// Get the next edit
		const record = stack[offset + 1];

		if (record) {
			// Apply the changes and update the offset
			this.updateInput(record);
			this.history.offset = Math.min(offset + 1, stack.length - 1);
		}
	};

	private handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		const { tabSize, insertSpaces, ignoreTabKey, onKeyDown } = this.props;

		if (onKeyDown) {
			onKeyDown(e);

			if (e.defaultPrevented) {
				return;
			}
		}

		if (e.keyCode === KEYCODE_ESCAPE) {
			e.currentTarget.blur();
		}

		const { value, selectionStart, selectionEnd } = e.currentTarget;

		const tabCharacter = (insertSpaces ? ' ' : '\t').repeat(tabSize);

		if (e.keyCode === KEYCODE_TAB && !ignoreTabKey && this.state.capture) {
			// Prevent focus change
			e.preventDefault();

			if (e.shiftKey) {
				// Unindent selected lines
				const linesBeforeCaret = this.getLines(value, selectionStart);
				const startLine = linesBeforeCaret.length - 1;
				const endLine = this.getLines(value, selectionEnd).length - 1;
				const nextValue = value
					.split('\n')
					.map((line, i) => {
						if (i >= startLine && i <= endLine && line.startsWith(tabCharacter)) {
							return line.substring(tabCharacter.length);
						}

						return line;
					})
					.join('\n');

				if (value !== nextValue) {
					const startLineText = linesBeforeCaret[startLine];

					this.applyEdits({
						value: nextValue,
						// Move the start cursor if first line in selection was modified
						// It was modified only if it started with a tab
						selectionStart: startLineText?.startsWith(tabCharacter)
							? selectionStart - tabCharacter.length
							: selectionStart,
						// Move the end cursor by total number of characters removed
						selectionEnd: selectionEnd - (value.length - nextValue.length)
					});
				}
			} else if (selectionStart !== selectionEnd) {
				// Indent selected lines
				const linesBeforeCaret = this.getLines(value, selectionStart);
				const startLine = linesBeforeCaret.length - 1;
				const endLine = this.getLines(value, selectionEnd).length - 1;
				const startLineText = linesBeforeCaret[startLine];

				this.applyEdits({
					value: value
						.split('\n')
						.map((line, i) => {
							if (i >= startLine && i <= endLine) {
								return tabCharacter + line;
							}

							return line;
						})
						.join('\n'),
					// Move the start cursor by number of characters added in first line of selection
					// Don't move it if it there was no text before cursor
					selectionStart:
						startLineText && /\S/.test(startLineText) ? selectionStart + tabCharacter.length : selectionStart,
					// Move the end cursor by total number of characters added
					selectionEnd: selectionEnd + tabCharacter.length * (endLine - startLine + 1)
				});
			} else {
				const updatedSelection = selectionStart + tabCharacter.length;

				this.applyEdits({
					// Insert tab character at caret
					value: value.substring(0, selectionStart) + tabCharacter + value.substring(selectionEnd),
					// Update caret position
					selectionStart: updatedSelection,
					selectionEnd: updatedSelection
				});
			}
		} else if (e.keyCode === KEYCODE_BACKSPACE) {
			const hasSelection = selectionStart !== selectionEnd;
			const textBeforeCaret = value.substring(0, selectionStart);

			if (textBeforeCaret.endsWith(tabCharacter) && !hasSelection) {
				// Prevent default delete behaviour
				e.preventDefault();

				const updatedSelection = selectionStart - tabCharacter.length;

				this.applyEdits({
					// Remove tab character at caret
					value: value.substring(0, selectionStart - tabCharacter.length) + value.substring(selectionEnd),
					// Update caret position
					selectionStart: updatedSelection,
					selectionEnd: updatedSelection
				});
			}
		} else if (e.keyCode === KEYCODE_ENTER) {
			// Ignore selections
			if (selectionStart === selectionEnd) {
				// Get the current line
				const line = this.getLines(value, selectionStart).pop();
				const matches = line?.match(/^\s+/);

				if (matches?.[0]) {
					e.preventDefault();

					// Preserve indentation on inserting a new line
					const indent = `\n${matches[0]}`;
					const updatedSelection = selectionStart + indent.length;

					this.applyEdits({
						// Insert indentation character at caret
						value: value.substring(0, selectionStart) + indent + value.substring(selectionEnd),
						// Update caret position
						selectionStart: updatedSelection,
						selectionEnd: updatedSelection
					});
				}
			}
		} else if (
			e.keyCode === KEYCODE_PARENS ||
			e.keyCode === KEYCODE_BRACKETS ||
			e.keyCode === KEYCODE_QUOTE ||
			e.keyCode === KEYCODE_BACK_QUOTE
		) {
			let chars;

			if (e.keyCode === KEYCODE_PARENS && e.shiftKey) {
				chars = ['(', ')'];
			} else if (e.keyCode === KEYCODE_BRACKETS) {
				if (e.shiftKey) {
					chars = ['{', '}'];
				} else {
					chars = ['[', ']'];
				}
			} else if (e.keyCode === KEYCODE_QUOTE) {
				if (e.shiftKey) {
					chars = ['"', '"'];
				} else {
					chars = [`'`, `'`];
				}
			} else if (e.keyCode === KEYCODE_BACK_QUOTE && !e.shiftKey) {
				chars = ['`', '`'];
			}

			// If text is selected, wrap them in the characters
			if (selectionStart !== selectionEnd && chars) {
				e.preventDefault();
				const vStart = value.substring(0, selectionStart);
				const vMiddle = value.substring(selectionStart, selectionEnd);
				const vEnd = value.substring(selectionEnd);

				this.applyEdits({
					value: `${vStart}${chars[0]}${vMiddle}${chars[1]}${vEnd}`,
					// Update caret position
					selectionStart,
					selectionEnd: selectionEnd + 2
				});
			}
		} else if (
			(isMacLike
				? // Trigger undo with ⌘+Z on Mac
				e.metaKey && e.keyCode === KEYCODE_Z
				: // Trigger undo with Ctrl+Z on other platforms
				e.ctrlKey && e.keyCode === KEYCODE_Z) &&
			!e.shiftKey &&
			!e.altKey
		) {
			e.preventDefault();

			this.undoEdit();
		} else if (
			(isMacLike
				? // Trigger redo with ⌘+Shift+Z on Mac
				e.metaKey && e.keyCode === KEYCODE_Z && e.shiftKey
				: isWindows
					? // Trigger redo with Ctrl+Y on Windows
					e.ctrlKey && e.keyCode === KEYCODE_Y
					: // Trigger redo with Ctrl+Shift+Z on other platforms
					e.ctrlKey && e.keyCode === KEYCODE_Z && e.shiftKey) &&
			!e.altKey
		) {
			e.preventDefault();

			this.redoEdit();
		} else if (e.keyCode === KEYCODE_M && e.ctrlKey && (isMacLike ? e.shiftKey : true)) {
			e.preventDefault();

			// Toggle capturing tab key so users can focus away
			this.setState((state) => ({
				capture: !state.capture
			}));
		}
	};

	private handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const { value, selectionStart, selectionEnd } = e.currentTarget;

		this.recordChange(
			{
				value,
				selectionStart,
				selectionEnd
			},
			true
		);

		this.props.onValueChange(value);
	};

	get session() {
		return {
			history: this.history
		};
	}

	set session(session: { history: History }) {
		this.history = session.history;
	}
}
